/**
 * @fileoverview Deployment IO — portable state holder (the node).
 * @module apps/network/global/io/nodes/deployment/deployment-io
 *
 * THE single point of exit for `@liquidefy/network-deployment`. The
 * only file in `apps/network` that imports the deployment engine.
 * Every slice portal is a LENS on what this node holds — none of
 * them re-import the engine (the `database-io.ts` precedent).
 *
 * THE DEFINING TRAIT — the two-plane wall is in the package itself:
 *   • `@liquidefy/network-deployment` (default `.`) = control-plane:
 *     browser-safe types, the topology model + validator/diff, the
 *     error taxonomy. Held DIRECTLY, synchronously, in-browser.
 *   • `@liquidefy/network-deployment/execution` (`createLiquidefy
 *     Deployment`) = execution-plane: daemon-only. A browser app
 *     MUST NOT import it. Effecting ops are DISPATCHED over an
 *     injected command port whose impl lives where the daemon runs.
 *
 * So this node is NOT a `createDatabaseIo`-style in-browser engine
 * constructor. It holds: (a) the control-plane surface directly,
 * (b) an OPTIONAL injected command/transport port for effecting
 * ops, (c) the live event stream. Absent command port ⇒ a TYPED
 * `NotWiredError` (the package's own browser-safe FACE-LEDGER
 * error) — never a fake result. Pure TS: zero DOM, zero Svelte.
 */

import {
  validateTopology,
  diffTopology,
  LIQUIDEFY_TOPOLOGY,
  NotWiredError,
} from "@liquidefy/network-deployment";
import type {
  LiquidefyTopology,
  SupplyRunSpec,
} from "@liquidefy/network-deployment";

export type {
  LiquidefyDeploymentIO,
  LiquidefyDeploymentCore,
  LiquidefyDeploymentSupply,
  LiquidefyDeploymentUplift,
  LiquidefyDeploymentDeps,
  ProvenanceLedgerEntry,
  LiquidefyTopology,
  TopologyValidation,
} from "@liquidefy/network-deployment";
export type * from "@liquidefy/network-deployment";

// Re-export the browser-safe topology fns so slice portals (e.g.
// TopologyIO) reach them THROUGH the node (principle 1 — only this
// file imports the engine).
export { validateTopology, diffTopology };

// ─── The contract (single-sourced here; portals re-export, never ──
// re-declare). The KEY + Context interface are framework-agnostic
// and live in the node; the Svelte-bound `useDeploymentIO()` hook
// (which calls `getContext`) lives in `DeploymentIO.svelte` and
// re-exports these — principle 4, the one-contract sync join.

export const DEPLOYMENT_IO_KEY = "deployment-io" as const;

/** One streamed unified event — the j-arc unified taxonomy
 *  projection (`origin/op/code/atMs`), NOT a forked shape. */
export interface DeploymentStreamEvent {
  readonly atMs: number;
  readonly origin: string;
  readonly op: string;
  readonly code: string;
  readonly detail?: unknown;
}

/** The browser-safe control-plane surface — always present (pure
 *  data + pure functions; zero secrets, zero `node:*`). */
export interface DeploymentControlSurface {
  readonly validateTopology: typeof validateTopology;
  readonly diffTopology: typeof diffTopology;
  readonly topology: LiquidefyTopology;
}

/** The injected execution-plane bridge. Its concrete impl lives
 *  where the daemon runs (wired at P2 over the control↔execution
 *  command channel) — NEVER imported into the browser node. Absent
 *  ⇒ control-plane-only; any effecting call is a typed refusal. */
export interface DeploymentCommandPort {
  dispatch(op: string, payload?: unknown): Promise<unknown>;
  subscribe(listener: (e: DeploymentStreamEvent) => void): () => void;
}

export interface DeploymentNodeInit {
  /** The injected effecting transport. Absent ⇒ FACE-LEDGER. */
  readonly command?: DeploymentCommandPort;
  /** Topology override; absent ⇒ the package's `LIQUIDEFY_TOPOLOGY`. */
  readonly topology?: LiquidefyTopology;
}

/** The root context shape the portal provides (and every consumer
 *  reads via `useDeploymentIO()`). */
export interface DeploymentIOContext {
  readonly ready: boolean;
  readonly initError: Error | null;
  readonly control: DeploymentControlSurface | null;
  /** True iff a command port is wired (effecting ops available). */
  readonly effecting: boolean;
  readonly stream: readonly DeploymentStreamEvent[];
  /** Dispatch an effecting op over the command channel. No port
   *  wired ⇒ a typed `NotWiredError` (honest, never a fake). */
  dispatch(op: string, payload?: unknown): Promise<unknown>;
}

// ─── P1 — control-plane read lens contracts (single-sourced here; ──
// each portal imports + re-exports, never re-declares — principle 4)

/** TopologyIO — the LIQUIDEFY_TOPOLOGY single-source-of-truth lens. */
export const TOPOLOGY_IO_KEY = "deployment-topology-io" as const;
export interface TopologyIOContext {
  /** True iff the parent node's control surface is ready. */
  readonly ready: boolean;
  /** The declared topology — `null` iff `!ready`. */
  readonly topology: LiquidefyTopology | null;
  /** Validate any topology declaration. Always callable. */
  readonly validate: typeof validateTopology;
  /** Diff two topology declarations. Always callable. */
  readonly diff: typeof diffTopology;
}

/** ObservabilityIO — the one-taxonomy operator surface lens
 *  (filters/groups/digest over the unified stream; never forks the
 *  taxonomy — the classifier is the daemon's authority). */
export const OBSERVABILITY_IO_KEY = "deployment-observability-io" as const;
export interface ObservabilityIOContext {
  /** The unified-event stream as the parent holds it. */
  readonly stream: readonly DeploymentStreamEvent[];
  /** Filter by `origin` (e.g. "ok", "runtime", "domain"). */
  byOrigin(origin: string): readonly DeploymentStreamEvent[];
  /** Filter by `op` (e.g. "cutover", "restoreToPoint"). */
  byOp(op: string): readonly DeploymentStreamEvent[];
  /** A deterministic, sorted `origin/code=count` digest. */
  digest(): readonly string[];
}

/** ProvenanceIO — the u1 fused ledger as an ordered timeline lens. */
export const PROVENANCE_IO_KEY = "deployment-provenance-io" as const;
export interface ProvenanceIOContext {
  /** The ordered append-only timeline (the daemon emits in order). */
  readonly events: readonly DeploymentStreamEvent[];
  /** The most recent event, or null if the stream is empty. */
  readonly latest: DeploymentStreamEvent | null;
  readonly count: number;
}

/** One of the eight first-principles lifecycle phases (k·L·m·n·o·p·q·r). */
export type LifecyclePhaseArc =
  | "k"
  | "L"
  | "m"
  | "n"
  | "o"
  | "p"
  | "q"
  | "r";
export type LifecyclePhaseStatus = "COMPLETE" | "PARTIAL" | "PENDING";
export interface LifecyclePhase {
  readonly arc: LifecyclePhaseArc;
  readonly name: string;
  readonly scope: string;
  readonly status: LifecyclePhaseStatus;
}

/** LifecycleIO — the meta-bar 8/8 board lens. */
export const LIFECYCLE_IO_KEY = "deployment-lifecycle-io" as const;
export interface LifecycleIOContext {
  readonly phases: readonly LifecyclePhase[];
  readonly completeCount: number;
  readonly total: number;
  /** Provenance of the asserted status (a live meta-bar feed lands
   *  in P2+; until then this is the network/deployment@1.9.0 truth
   *  shipped at the r-arc grand-capstone trigger 4e040ad). */
  readonly assertedFrom: string;
}

/** The current lifecycle truth — sourced from network/deployment
 *  @1.9.0 (8/8 ENFORCE PERMANENT; trigger 4e040ad). A live feed
 *  will replace this constant in P2+ once the command channel
 *  streams meta-bar verdicts. Honest staged surface, not a stub. */
export const LIFECYCLE_PHASES: readonly LifecyclePhase[] = [
  {
    arc: "k",
    name: "Database deploy",
    scope: "Per-app DB provision/migrate/branch/snapshot/drift/teardown.",
    status: "COMPLETE",
  },
  {
    arc: "L",
    name: "Storage + Drive",
    scope: "Per-app store + buckets/quota/folder-tree/share/snapshot.",
    status: "COMPLETE",
  },
  {
    arc: "m",
    name: "Application release",
    scope:
      "Resolve+pin → plan → roll → cutover+auto-rollback (strategies+drain).",
    status: "COMPLETE",
  },
  {
    arc: "n",
    name: "Traffic / DNS / TLS",
    scope:
      "DNS plan+apply, cert issue gate, bind, health-gated shift, renewal.",
    status: "COMPLETE",
  },
  {
    arc: "o",
    name: "App cache substrate",
    scope: "Provision/connect/bind/ops/drain/drift/teardown.",
    status: "COMPLETE",
  },
  {
    arc: "p",
    name: "App queue substrate",
    scope: "Provision/connect/bind/ops/drain/drift/teardown.",
    status: "COMPLETE",
  },
  {
    arc: "q",
    name: "Telemetry export (LAYER)",
    scope:
      "Unified taxonomy fanned to an injected OTel sink (W2-safe).",
    status: "COMPLETE",
  },
  {
    arc: "r",
    name: "Coordinated rollback + supply chain",
    scope:
      "Atomic W3-ordered restoreToPoint + gated artifact supply chain.",
    status: "COMPLETE",
  },
] as const;

// ─── P2 — the deploy-loop effecting lens contracts (single-sourced ──
// here; each portal imports + re-exports, never re-declares).
// EVERY effecting action routes through `parent.dispatch(op, …)`;
// no portal calls the execution plane directly — the two-plane
// wall holds. Unwired ⇒ a typed `NotWiredError` (FACE-LEDGER).

/** TriggerIO — Decision A: the canonical `runTrigger` (unified
 *  gated/ledgered supply+ship as one substrate run). */
export const TRIGGER_IO_KEY = "deployment-trigger-io" as const;
export interface TriggerIOContext {
  /** A trigger is currently in flight. */
  readonly inFlight: boolean;
  /** The most recent successful result (opaque). */
  readonly lastResult: unknown;
  readonly lastError: Error | null;
  /** Chronological stream events emitted by `runTrigger` ops. */
  readonly history: readonly DeploymentStreamEvent[];
  /** Dispatch a `runTrigger` over the command channel. Rejects
   *  with `NotWiredError` if the port is absent (FACE-LEDGER). */
  trigger(spec: SupplyRunSpec): Promise<unknown>;
}

/** PreviewPlanIO — u3 plan-before-apply: `previewRun` →
 *  diff-gate; `previewOnly` (never proceed); `previewFork`. */
export const PREVIEW_PLAN_IO_KEY = "deployment-preview-plan-io" as const;
export interface PreviewPlanIOContext {
  readonly inFlight: boolean;
  /** The most recent diff/plan returned (opaque). */
  readonly lastDiff: unknown;
  readonly lastError: Error | null;
  readonly history: readonly DeploymentStreamEvent[];
  preview(spec: SupplyRunSpec): Promise<unknown>;
  previewOnly(spec: SupplyRunSpec): Promise<unknown>;
  previewFork(
    spec: SupplyRunSpec,
    fork: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

/** The 5 declared u2 gate dimensions (every verdict ledgered). */
export type GateDimension =
  | "budget"
  | "freeze"
  | "health"
  | "attestation"
  | "approval";

/** GatesIO — u2 gate tier: budget ENFORCED, change-freeze, health,
 *  attestation, approval. Every verdict ledgered. */
export const GATES_IO_KEY = "deployment-gates-io" as const;
export interface GatesIOContext {
  /** The 5 declared gate dimensions (the u2 v1.9.0 truth). */
  readonly dimensions: readonly GateDimension[];
  readonly inFlight: boolean;
  /** The most recent gate verdict (opaque). */
  readonly lastVerdict: unknown;
  readonly lastError: Error | null;
  /** Streamed verdicts (filtered to gate-related ops). */
  readonly verdicts: readonly DeploymentStreamEvent[];
  /** Run a gated deploy through the u2 gate tier. */
  gatedDeploy(spec: SupplyRunSpec): Promise<unknown>;
}

/** ApprovalsIO — u4 human-in-the-loop: deduped notifications +
 *  approval round-trip + resolve. */
export const APPROVALS_IO_KEY = "deployment-approvals-io" as const;
export interface ApprovalsIOContext {
  readonly inFlight: boolean;
  readonly lastError: Error | null;
  /** Pending approval-request events streamed from the daemon. */
  readonly pending: readonly DeploymentStreamEvent[];
  /** Decided approval events (approved/denied). */
  readonly decisions: readonly DeploymentStreamEvent[];
  approve(approvalId: string, note?: string): Promise<unknown>;
  deny(approvalId: string, reason: string): Promise<unknown>;
  /** Resolve a deduped notification (close the human loop). */
  resolve(approvalId: string): Promise<unknown>;
}

// ─── P3 — the 8 arc effecting lens contracts ──────────────────────
// Each arc dispatches a prefixed op family (`release.*`, `traffic.*`,
// `db.*`, `storage.*`, `cache.*`, `queue.*`, `telemetry.*`,
// `rollback.*`) over the command port. Stream history is filtered by
// prefix per arc — clean discriminator across arcs.

/** Reference to a deploy target — opaque except for its id. The
 *  typed first param of most arc actions. */
export interface DeployTargetRef {
  readonly id: string;
}

/** Reference to a restore point (the r-arc coordinate). */
export interface RestorePointRef {
  readonly id: string;
  readonly atMs?: number;
}

/** TelemetryIO mode (q-arc, `LQD_TELEMETRY` projection). */
export type TelemetryMode = "off" | "export";

/** Common effecting-lens shape — every arc portal exposes these. */
interface EffectingLensBase {
  readonly inFlight: boolean;
  readonly lastResult: unknown;
  readonly lastError: Error | null;
  readonly history: readonly DeploymentStreamEvent[];
}
// (kept local; portals expose their own typed Context interface
//  that extends this shape with the arc's actions.)

/** ReleaseIO (m) — application release workflow. */
export const RELEASE_IO_KEY = "deployment-release-io" as const;
export interface ReleaseIOContext extends EffectingLensBase {
  release(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  cutover(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  drain(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  rollbackApp(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** TrafficIO (n) — traffic / DNS / TLS workflow. */
export const TRAFFIC_IO_KEY = "deployment-traffic-io" as const;
export interface TrafficIOContext extends EffectingLensBase {
  planDns(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  applyDns(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  awaitCertIssued(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  bind(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  shift(target: DeployTargetRef, percent: number, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  revert(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  renewCert(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** DatabaseDeployIO (k) — app database deploy workflow. */
export const DATABASE_DEPLOY_IO_KEY = "deployment-database-deploy-io" as const;
export interface DatabaseDeployIOContext extends EffectingLensBase {
  provision(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  connect(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  migrate(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  seed(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  branch(target: DeployTargetRef, branchId: string, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  snapshot(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  restore(target: DeployTargetRef, snapshotId: string, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  teardown(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** StorageIO (L) — storage deploy + Drive platform. */
export const STORAGE_IO_KEY = "deployment-storage-io" as const;
export interface StorageIOContext extends EffectingLensBase {
  provisionBucket(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  connect(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  folderCreate(target: DeployTargetRef, path: string, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  folderMove(target: DeployTargetRef, from: string, to: string): Promise<unknown>;
  folderTrash(target: DeployTargetRef, path: string): Promise<unknown>;
  share(target: DeployTargetRef, fileRef: string, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  snapshot(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  restore(target: DeployTargetRef, snapshotId: string, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  teardown(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** CacheIO (o) — app cache substrate workflow. */
export const CACHE_IO_KEY = "deployment-cache-io" as const;
export interface CacheIOContext extends EffectingLensBase {
  provision(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  connect(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  warm(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  invalidate(target: DeployTargetRef, key: string): Promise<unknown>;
  flush(target: DeployTargetRef): Promise<unknown>;
  drain(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  teardown(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** QueueIO (p) — app queue substrate workflow. */
export const QUEUE_IO_KEY = "deployment-queue-io" as const;
export interface QueueIOContext extends EffectingLensBase {
  provision(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  connect(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  publish(target: DeployTargetRef, message: Readonly<Record<string, unknown>>): Promise<unknown>;
  peek(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  purge(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  drain(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  teardown(target: DeployTargetRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** TelemetryIO (q) — telemetry export LAYER (LAYER arc; mode switch). */
export const TELEMETRY_IO_KEY = "deployment-telemetry-io" as const;
export interface TelemetryIOContext extends EffectingLensBase {
  setMode(mode: TelemetryMode, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** RollbackIO (r) — coordinated cross-substrate rollback + supply. */
export const ROLLBACK_IO_KEY = "deployment-rollback-io" as const;
export interface RollbackIOContext extends EffectingLensBase {
  restoreToPoint(point: RestorePointRef, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  runSupplyChain(artifactRef: string, options?: Readonly<Record<string, unknown>>): Promise<unknown>;
}

// ─── P4 — the 11 cross-cutting effecting lens contracts ──────────
// Each dispatches a `<concern>.*` op family over the command port,
// same pattern as P2/P3. Two also surface a filtered stream view.

/** PublishIO (u5) — publish pipeline fan-out + report-DLQ +
 *  scan→attestation gate. */
export const PUBLISH_IO_KEY = "deployment-publish-io" as const;
export interface PublishIOContext extends EffectingLensBase {
  publishAll(spec: Readonly<Record<string, unknown>>): Promise<unknown>;
  publishOne(
    target: DeployTargetRef,
    payload: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

/** CutoverIO (u6) — zero-downtime cutover waves + pre-cutover
 *  backup + health-gate rollback + backup-prune. */
export const CUTOVER_IO_KEY = "deployment-cutover-io" as const;
export interface CutoverIOContext extends EffectingLensBase {
  cutover(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  backupBefore(target: DeployTargetRef): Promise<unknown>;
  backupPrune(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

/** ReconcileIO (u7) — supervised continuous reconcile + drift→notify. */
export const RECONCILE_IO_KEY = "deployment-reconcile-io" as const;
export interface ReconcileIOContext extends EffectingLensBase {
  reconcileOnce(target?: DeployTargetRef): Promise<unknown>;
  start(options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  stop(): Promise<unknown>;
  /** Drift-event projection of the parent stream. */
  readonly drift: readonly DeploymentStreamEvent[];
}

/** EphemeralIO (Decision B) — ephemeral preview envs (webhook + TTL). */
export const EPHEMERAL_IO_KEY = "deployment-ephemeral-io" as const;
export interface EphemeralIOContext extends EffectingLensBase {
  spin(
    branchRef: string,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  teardown(envId: string): Promise<unknown>;
  /** Live ephemeral-env events (spin/teardown/ttl) projected. */
  readonly live: readonly DeploymentStreamEvent[];
}

/** CredentialsIO — credential lifecycle + rotate-as-a-deploy. */
export const CREDENTIALS_IO_KEY = "deployment-credentials-io" as const;
export interface CredentialsIOContext extends EffectingLensBase {
  rotate(
    credentialRef: string,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

// ─── TokensIO — user-facing API token surface (TOKENS Phase 1) ─────
// Distinct from CredentialsIO (which is about ROTATING provider
// secrets via rotate-as-a-deploy). TokensIO manages `lqd_<env>_…`
// tokens that the user mints for CLI / CI / programmatic access.
//
// Dispatches: tokens.list / .create / .revoke / .rotate / .uses / .stats
// Server contract: network/servers/deployment/0.1.0/src/dispatch/tokens.ts
// Design canon: operating-system/services/tokens/TOKENS.md

/** Safe token row — never includes the hash or the rendered token. */
export interface TokensRowPublic {
  readonly tokenId: string;
  readonly prefixVisible: string;
  readonly label: string;
  readonly category: "ci" | "cli" | "integration" | "personal" | "service";
  readonly env: "test" | "live";
  readonly scopes: readonly string[];
  readonly projectScopeIds: readonly string[] | null;
  readonly createdAtMs: number;
  readonly expiresAtMs: number | null;
  readonly lastUsedAtMs: number | null;
  readonly lastUsedIp: string | null;
  readonly lastUsedOp: string | null;
  readonly revokedAtMs: number | null;
  readonly revokedReason: string | null;
}

/** The rendered token — returned EXACTLY ONCE at create / rotate.
 *  Once this leaves the dispatch result it must not be persisted
 *  outside the user's clipboard / env file / password manager. */
export interface TokensRendered extends TokensRowPublic {
  /** The full `lqd_<env>_<28-char-body>` string. */
  readonly rendered: string;
}

export interface TokensUseEntry {
  readonly useId: string;
  readonly tokenId: string;
  readonly op: string;
  readonly matchedScope: string | null;
  readonly outcome: "allowed" | "denied" | "denied-project-scope" | "denied-revoked" | "denied-expired";
  readonly ip: string | null;
  readonly detail: Readonly<Record<string, unknown>> | null;
  readonly atMs: number;
}

export interface TokensCreateInput {
  readonly label: string;
  readonly category?: TokensRowPublic["category"];
  readonly env?: "test" | "live";
  /** One of "read" | "deploy" | "admin". */
  readonly preset?: "read" | "deploy" | "admin";
  /** Explicit scope list — overrides `preset` when present. */
  readonly scopes?: readonly string[];
  /** Optional project-scope restriction. Empty array / null = unrestricted. */
  readonly projectScopeIds?: readonly string[] | null;
  /** TTL in ms from now; null/undefined = never expires. */
  readonly ttlMs?: number | null;
}

export const TOKENS_IO_KEY = "deployment-tokens-io" as const;
export interface TokensIOContext extends EffectingLensBase {
  list(): Promise<{ readonly tokens: readonly TokensRowPublic[] }>;
  create(input: TokensCreateInput): Promise<TokensRendered>;
  revoke(tokenId: string, reason?: string): Promise<{ readonly tokenId: string; readonly revoked: boolean }>;
  rotate(tokenId: string): Promise<TokensRendered & { readonly oldTokenId: string; readonly newTokenId: string }>;
  uses(tokenId: string, limit?: number): Promise<{ readonly tokenId: string; readonly uses: readonly TokensUseEntry[] }>;
  stats(tokenId: string): Promise<{
    readonly tokenId: string;
    readonly total: number;
    readonly allowed: number;
    readonly denied: number;
    readonly distinctIps: number;
    readonly topOps: readonly { readonly op: string; readonly count: number }[];
  }>;
}

// ─── PlatformCredentialsIO — admin-only env-var probe surface ─────
// The operator-facing complement to TokensIO. Where TokensIO lets
// a user manage *their* tokens, PlatformCredentialsIO lets the
// admin operator inspect platform-wide vendor secrets (Cloudflare,
// Fly, Stripe, …) — see if each is set, healthy, recently used.
//
// Dispatches: creds.list / .probe / .rotationGuide
// Server contract: network/servers/deployment/0.1.0/src/dispatch/credentials.ts

export type PlatformCredentialStatus = "ok" | "warn" | "missing" | "error";
export interface PlatformCredentialHealth {
  readonly status: PlatformCredentialStatus;
  readonly detail?: string;
}
export interface PlatformCredentialEntry {
  readonly id: string;
  readonly label: string;
  readonly group: "cloudflare" | "fly" | "stripe" | "resend" | "gitea" | "storage" | "supabase" | "auth";
  readonly envVars: readonly string[];
  readonly description: string;
  readonly rotationGuide: string | null;
  readonly health: PlatformCredentialHealth;
  readonly probedAtMs: number;
}

export const PLATFORM_CREDENTIALS_IO_KEY = "deployment-platform-credentials-io" as const;
export interface PlatformCredentialsIOContext extends EffectingLensBase {
  list(): Promise<{ readonly credentials: readonly PlatformCredentialEntry[] }>;
  probe(credId: string): Promise<PlatformCredentialEntry>;
  rotationGuide(credId: string): Promise<{
    readonly id: string;
    readonly guide: string | null;
    readonly markdown: string | null;
    readonly path?: string;
    readonly error?: string;
  }>;
}

/** BindingIO — conformance-gated driver binding (reverify trigger). */
export const BINDING_IO_KEY = "deployment-binding-io" as const;
export interface BindingIOContext extends EffectingLensBase {
  reverify(seam: string): Promise<unknown>;
}

/** BootstrapIO (n7) — one-shot raw-cred cold-start ceremony. */
export const BOOTSTRAP_IO_KEY = "deployment-bootstrap-io" as const;
export interface BootstrapIOContext extends EffectingLensBase {
  bootstrap(seed: Readonly<Record<string, unknown>>): Promise<unknown>;
}

/** OpsIO (e-arc) — daemon ops runtime: heartbeat, breakers, HA. */
export const OPS_IO_KEY = "deployment-ops-io" as const;
export interface OpsIOContext extends EffectingLensBase {
  pause(): Promise<unknown>;
  resume(): Promise<unknown>;
  degrade(
    capability: string,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

/** SchedulingIO (i-arc) — scheduling brain: cron, freeze windows. */
export const SCHEDULING_IO_KEY = "deployment-scheduling-io" as const;
export interface SchedulingIOContext extends EffectingLensBase {
  addFreezeWindow(
    window: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  removeFreezeWindow(windowId: string): Promise<unknown>;
  cronSet(name: string, expr: string): Promise<unknown>;
}

/** PersistenceIO (j-arc) — durable SoR: migrations, backup, PITR. */
export const PERSISTENCE_IO_KEY = "deployment-persistence-io" as const;
export interface PersistenceIOContext extends EffectingLensBase {
  runMigration(
    name: string,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  backup(options?: Readonly<Record<string, unknown>>): Promise<unknown>;
  pitr(
    targetAtMs: number,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

/** SupplyChainIO (r-supply) — artifact build→scan→sign→push→pin. */
export const SUPPLY_CHAIN_IO_KEY = "deployment-supply-chain-io" as const;
export interface SupplyChainIOContext extends EffectingLensBase {
  run(
    artifactRef: string,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  scan(artifactRef: string): Promise<unknown>;
  sign(artifactRef: string): Promise<unknown>;
  push(artifactRef: string): Promise<unknown>;
}

export interface DeploymentNode {
  readonly getReady: () => boolean;
  readonly getInitError: () => Error | null;
  readonly getControl: () => DeploymentControlSurface | null;
  readonly getCommand: () => DeploymentCommandPort | null;
  readonly getStream: () => readonly DeploymentStreamEvent[];
  readonly hasEffecting: () => boolean;
  readonly dispatch: (op: string, payload?: unknown) => Promise<unknown>;
  readonly subscribe: (listener: () => void) => () => void;
  readonly initialize: () => Promise<void>;
  readonly destroy: () => void;
}

export function createDeploymentNode(
  init?: DeploymentNodeInit,
): DeploymentNode {
  let ready = false;
  let initError: Error | null = null;
  let control: DeploymentControlSurface | null = null;
  let destroyed = false;

  const command: DeploymentCommandPort | null = init?.command ?? null;
  let stream: readonly DeploymentStreamEvent[] = [];
  let unsubCommand: (() => void) | null = null;

  const listeners = new Set<() => void>();

  function notify(): void {
    for (const listener of Array.from(listeners)) {
      try {
        listener();
      } catch {
        /* swallow */
      }
    }
  }

  // The control plane is pure + browser-safe ⇒ build it eagerly and
  // synchronously. It is ALWAYS available (read/plan never depends
  // on the daemon — that is the whole point of the two-plane wall).
  try {
    control = {
      validateTopology,
      diffTopology,
      topology: init?.topology ?? LIQUIDEFY_TOPOLOGY,
    };
    ready = true;
  } catch (err) {
    initError = err instanceof Error ? err : new Error(String(err));
  }

  return {
    getReady: () => ready,
    getInitError: () => initError,
    getControl: () => (ready ? control : null),
    getCommand: () => command,
    getStream: () => stream,
    hasEffecting: () => command !== null,
    dispatch(op, payload) {
      if (!command) {
        // FACE-LEDGER — honest staged surface, not a stub.
        return Promise.reject(
          new NotWiredError(
            `dispatch(${op})`,
            "P2-command-channel",
            "no execution-plane command port injected (control-" +
              "plane-only build)",
          ),
        );
      }
      return command.dispatch(op, payload);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async initialize() {
      if (command && !unsubCommand) {
        unsubCommand = command.subscribe((e) => {
          stream = [...stream, e];
          notify();
        });
      }
      return;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (unsubCommand) {
        try {
          unsubCommand();
        } catch {
          /* swallow */
        }
      }
      unsubCommand = null;
      control = null;
      ready = false;
      stream = [];
      notify();
      listeners.clear();
    },
  };
}
